import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Button, Divider, Modal, Space } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  ListPlanAccountsQuery,
  PlanAccount,
  PlanAccountOrderBy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import {
  useDeletePlanAccount,
  usePlanAccounts,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.hooks';

import { PageProps } from '@/types/PageProps';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import AccountsPlanAccountsGeneralModalForm from '@/components/Financial/Accounts/PlanAccounts/General/ModalForm';
import PlanAccountReportModal from '@/components/Financial/Accounts/PlanAccounts/PlanAccountReportModal';
import { AppPageHeader } from '@/components/_common';
import PlanAccountsExtraFilters from '@/components/Financial/Accounts/PlanAccounts/PlanAccountsExtraFilters';
import { useDebounce, useDisclosure } from '@/hooks';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import PlanAccountImportModal from '@/components/Financial/Accounts/PlanAccounts/PlanAccountImportModal';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { tableSorterOrder } from '@/utils/table';

const PlanAccountsPage = ({ route }: PageProps) => {
  const [modalReportVisible, modalReportVisibleAction] = useDisclosure();
  const [modalGeneralVisible, modalGeneralVisibleAction] = useDisclosure();
  const [modalImportPlanAccount, modalImportPlanAccountActions] =
    useDisclosure();
  const [planAccountIdSelected, setPlanAccountIdSelected] = useState<number>();

  const [queryParams, setQueryParams] = useState<ListPlanAccountsQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<ListPlanAccountsQuery>({
      description: 'string',
      active: 'boolean',
      classification: 'string',
      search: 'string',
      destination: 'string',
      hierarchy: 'string',
      type: 'string',
      order: 'string',
    }),
  });

  const intl = useIntl();

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
    usePlanAccounts(queryDebounce);

  const planAccounts = flatInfiniteData(data);

  const toggleModalReport = () => {
    modalReportVisibleAction.toggle();
  };

  const toggleModalGeneral = () => {
    modalGeneralVisibleAction.toggle();
    setPlanAccountIdSelected(undefined);
  };

  const handleEditPlanAccount = (planAccountId: number) => {
    setPlanAccountIdSelected(planAccountId);
    modalGeneralVisibleAction.toggle();
  };

  const removeMutation = useDeletePlanAccount();

  const handleRemoveClick = (planAccount: PlanAccount) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'planAccounts.remove',
      }),
      content: `${intl.formatMessage({
        id: 'planAccounts.remove.alert',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(planAccount.id);

        refetch();
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<PlanAccount> = [
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.code' }),
      dataIndex: 'code',
      key: 'CODE',
      width: 100,
      sorter: true,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.description' }),
      dataIndex: 'description',
      key: 'DESCRIPTION',
      render: (value) => value || '-',
      ellipsis: true,
      width: 100,
      sorter: true,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.hierarchy' }),
      dataIndex: 'hierarchy',
      key: 'hierarchy',
      render: (value) =>
        intl.formatMessage({
          id: `planAccounts.modal.general.form.hierarchy.${value.toLowerCase()}`,
        }),
      width: 100,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.destination' }),
      dataIndex: 'destination',
      key: 'destination',
      render: (value) =>
        intl.formatMessage({
          id: `planAccounts.modal.general.form.destination.${value.toLowerCase()}`,
        }),
      width: 100,
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <EditIcon
            iconProps={{ onClick: () => handleEditPlanAccount(record.id) }}
            disabled={!hasPermissions([Authorities.REGISTER_PLAN_ACCOUNT_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={
              !hasPermissions([Authorities.REGISTER_PLAN_ACCOUNT_DELETE])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      persistSearchParams={false}
      route={route.parent}
      extra={
        <SpaceForButton>
          <Button
            disabled={
              !hasPermissions([Authorities.REGISTER_PLAN_ACCOUNT_REPORTS])
            }
            block
            type="primary"
            onClick={toggleModalReport}
          >
            <FormattedMessage id="planAccounts.generateReport" />
          </Button>

          <Button
            type="primary"
            block
            onClick={toggleModalGeneral}
            disabled={!hasPermissions([Authorities.REGISTER_PLAN_ACCOUNT_ADD])}
          >
            <FormattedMessage id="planAccounts.create" />
          </Button>

          {!planAccounts.length && (
            <Button
              type="primary"
              block
              onClick={modalImportPlanAccountActions.open}
            >
              Importar plano de contas
            </Button>
          )}
        </SpaceForButton>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() =>
                setQueryParams({
                  order: 'ASC',
                })
              }
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <PlanAccountsExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<PlanAccount>
        columns={columns}
        dataSource={planAccounts}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            setQueryParams((prev) => {
              const order = sorter.order
                ? tableSorterOrder[sorter.order]
                : undefined;

              const orderBy =
                PlanAccountOrderBy[sorter.columnKey as PlanAccountOrderBy];

              return { ...prev, order, orderBy };
            });
          }
        }}
      />

      <PlanAccountReportModal
        visible={modalReportVisible}
        handleVisible={toggleModalReport}
      />

      <PlanAccountImportModal
        onCancel={modalImportPlanAccountActions.close}
        open={modalImportPlanAccount}
      />

      <AccountsPlanAccountsGeneralModalForm
        planAccountId={planAccountIdSelected}
        visible={modalGeneralVisible}
        onCancel={toggleModalGeneral}
      />
    </DefaultRouteTabs>
  );
};

export default PlanAccountsPage;
